import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { TimeEntryProps } from "../types/interfaces";
import { timeEntryKeys } from "../utils/queryKeys";

interface TimeEntryFilters {
  userId?: string;
  taskId?: string;
  projectId?: string;
  from?: string;
  to?: string;
}

function toQueryString(filters: TimeEntryFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return params.toString();
}

export function useTimeEntries(filters: TimeEntryFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: timeEntryKeys.list({ ...filters }),
    queryFn: async () => (await apiRequest<TimeEntryProps[]>(`time-entries?${toQueryString(filters)}`)).data,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateTimeEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<TimeEntryProps> & { taskId: string; date: string; durationMinutes: number }) =>
      (await apiRequest<TimeEntryProps>("time-entries", { method: "POST", body: JSON.stringify(data) })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timeEntryKeys.all }),
  });
}

export function useUpdateTimeEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TimeEntryProps> }) =>
      (await apiRequest<TimeEntryProps>(`time-entries/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timeEntryKeys.all }),
  });
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiRequest<void>(`time-entries/${id}`, { method: "DELETE" })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timeEntryKeys.all }),
  });
}
